import { useEffect, useRef, useCallback } from 'react';
import Plyr from 'plyr';
import 'plyr/dist/plyr.css';
import { triggerDownload } from '@/utils/download';
import { ViewerShell } from './ViewerShell';
import type { FavoriteCachedData } from '@/types/favorites';

interface VideoPlayerProps {
  url: string;
  title?: string;
  fileName?: string;
  mimeType?: string;
  onClose: () => void;
  onDownload?: () => void;
  onProgress?: (progress: number) => void;
  fileId?: string;
  favoriteCachedData?: FavoriteCachedData;
}

export function VideoPlayer({
  url,
  title,
  fileName,
  mimeType,
  onClose,
  onDownload,
  onProgress,
  fileId,
  favoriteCachedData,
}: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const playerRef = useRef<Plyr | null>(null);

  useEffect(() => {
    if (videoRef.current && !playerRef.current) {
      playerRef.current = new Plyr(videoRef.current, {
        controls: [
          'play-large',
          'play',
          'progress',
          'current-time',
          'duration',
          'mute',
          'volume',
          'captions',
          'settings',
          'pip',
          'airplay',
          'fullscreen',
        ],
        settings: ['quality', 'speed'],
        speed: { selected: 1, options: [0.5, 0.75, 1, 1.25, 1.5, 2] },
        keyboard: { focused: true, global: true },
        tooltips: { controls: true, seek: true },
      });

      if (onProgress) {
        const handleTimeUpdate = () => {
          const player = playerRef.current;
          if (player && player.duration > 0) {
            onProgress((player.currentTime / player.duration) * 100);
          }
        };
        playerRef.current.on('timeupdate', handleTimeUpdate);
      }
    }

    return () => {
      playerRef.current?.destroy();
      playerRef.current = null;
    };
  }, [onProgress]);

  const handleDownload = useCallback(() => {
    if (onDownload) {
      onDownload();
    } else {
      triggerDownload(url, fileName || title || 'video');
    }
  }, [url, fileName, title, onDownload]);

  const getSourceType = (): string => {
    if (mimeType) return mimeType;
    const ext = url.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'mp4':
        return 'video/mp4';
      case 'webm':
        return 'video/webm';
      case 'ogg':
        return 'video/ogg';
      case 'mov':
        return 'video/quicktime';
      default:
        return 'video/mp4';
    }
  };

  return (
    <ViewerShell
      title={title || 'Video'}
      type="video"
      fileId={fileId}
      favoriteCachedData={favoriteCachedData}
      onClose={onClose}
      onDownload={handleDownload}
      keyboardHint="Space to play/pause • Arrow keys to seek • M to mute • Esc to close"
    >
      <div className="flex-1 flex items-center justify-center p-2 sm:p-4 overflow-hidden min-h-full">
        <div className="w-full max-w-4xl">
          <video ref={videoRef} className="plyr-react plyr w-full" playsInline>
            <source src={url} type={getSourceType()} />
            Your browser does not support the video tag.
          </video>
        </div>
      </div>
    </ViewerShell>
  );
}
